import React from 'react';
import {
  Colors,
  Helpers,
  Metrics,
  Spinner,
  Tab,
  ControlledTabs,
  Text,
  BREAK_POINTS
} from '@origin-digital/style-guide';
import { Box } from '@origin-digital/layout';
import styled from 'styled-components';
import Grid from '../../../../components/MuiGrid/Grid';
import { CalculatorResultProps, ICalculatorResultState } from './CalculatorResult.types';
import { ResultTab } from './ResultTab/ResultTab';
import WhatsNext from './WhatsNext';
import { isIE11 } from '../../../../utils/isIE/isIE11';

const Container = styled.div`
  position: relative;
  line-height: 24px;
  margin: auto;
  padding-top: ${Metrics.Spacing.M_X_LG}px;
  box-sizing: border-box;
  display: none;

  &.loaded {
    display: block;
  }

  ${Helpers.media.LG`
    display: block;
    margin-left: ${Metrics.Spacing.M_X_LG}px;
    margin-right: 0;
    padding: 0;
  `}
  ${Helpers.media.XL`
    margin-left: ${Metrics.Spacing.M_X_LG}px;
  `}
`;

const Loading = styled.div`
  width: 100%;
  height: 100%;
  display: -ms-grid;
  display: grid;
  align-items: center;
  justify-content: center;

  &,
  &:before {
    position: absolute;
    top: 0;
    left: 0;
  }

  * {
    position: relative;
  }

  &:before {
    content: '';
    opacity: 0.8;
    left: ${Metrics.Spacing.M_Y_SM}px;
    right: ${Metrics.Spacing.M_Y_SM}px;
    top: ${Metrics.Spacing.M_Y_SM}px;
    bottom: ${Metrics.Spacing.M_Y_SM}px;
    background: ${Colors.WHITE};
  }

  ${Helpers.media.LG`
    &:before {
      top: 0;
    }
  `};
`;

export class CalculatorResult extends React.Component<
  CalculatorResultProps,
  ICalculatorResultState
> {
  private scrollMarker: React.RefObject<HTMLDivElement>;

  constructor(props: CalculatorResultProps) {
    super(props);
    this.scrollMarker = React.createRef();
  }

  private getActiveTab = (): string => {
    const { recommendations: result } = this.props;
    const { primaryRecommendationId, recommendations } = result;
    const defaultTab = recommendations!.find(rec => rec!.id === primaryRecommendationId);
    return defaultTab.id;
  };

  state = {
    activeTab: this.getActiveTab(),
    activeTabSize: this.props.activeTabSize
  };

  public componentDidUpdate(): void {
    if (this.props.fromPackageSection) {
      this.scrollTo();
    }
  }

  public render() {
    const {
      recommendations: result,
      loaded,
      values,
      onShowDisclaimer,
      calculationCount,
      submit,
      forDate,
      fromPackageSection,
      activeTabSize
    } = this.props;

    const { primaryRecommendationId, recommendations } = result;

    const defaultTab = recommendations!.find(rec => rec!.id === primaryRecommendationId);

    const refreshActiveTab = recommendations!.find(rec => rec!.id === this.state.activeTab);
    let activeTab = refreshActiveTab ? this.state.activeTab : defaultTab!.id;
    if (activeTabSize) {
      if (fromPackageSection) {
        activeTab = recommendations!.find(rec => rec!.id!.includes(this.state.activeTabSize))!.id;
      } else {
        activeTab = this.state.activeTab;
      }
    } else {
      activeTab = defaultTab.id;
    }

    const tabsKey = `${activeTab}${calculationCount})`;

    return (
      <div ref={this.scrollMarker}>
        <Container className={loaded ? 'loaded' : ''}>
          <ControlledTabs
            key={tabsKey}
            activeTab={activeTab}
            onTabChange={this.changeTab}
            flexMode="allMedia"
          >
            {recommendations!.map(rec => {
              const solarSystemId: string = (rec && rec.solarSystem && rec.solarSystem.id) || '';
              const payoffYears: number =
                (rec && rec.solarSaving && rec.solarSaving.payoffTimeYears) || 0;
              const recommended = solarSystemId === primaryRecommendationId && payoffYears <= 10;
              return (
                <Tab
                  key={rec!.id}
                  id={rec!.id}
                  data-id={rec!.id}
                  title={
                    <Box marginTop={-3} marginBottom={-2}>
                      <Grid container direction="column" alignContent="center">
                        <Grid item>
                          <Text size="md" fontWeight={rec!.id === activeTab ? 'bold' : 'regular'}>
                            {rec!.solarSystem!.size}
                          </Text>
                        </Grid>
                        <Grid item>
                          <Text size="xs" color="grey80">
                            {rec!.solarSystem!.systemWatts / 1000} kw
                          </Text>
                        </Grid>
                      </Grid>
                    </Box>
                  }
                >
                  <ResultTab
                    recommendation={rec!}
                    recommended={recommended}
                    values={values}
                    onShowDisclaimer={onShowDisclaimer}
                    submit={submit}
                    forDate={forDate}
                  />
                </Tab>
              );
            })}
          </ControlledTabs>
          {this.props.loading ? (
            <Loading>
              <Spinner size={48} />
            </Loading>
          ) : null}
          <Box marginTop={6} marginBottom={6}>
            {!isIE11() && <WhatsNext />}
          </Box>
        </Container>
      </div>
    );
  }

  private changeTab = (id: string) => {
    const tabSize = id.includes('mini')
      ? 'mini'
      : id.includes('small')
      ? 'small'
      : id.includes('medium')
      ? 'medium'
      : 'large';
    this.props.updateActiveTabSize(tabSize);
    this.props.updateFromPackageCall(false);
    this.setState({ activeTab: id, activeTabSize: tabSize });
  };

  private scrollTo = () => {
    if (window.innerWidth < parseInt(BREAK_POINTS.Min.LG, 0)) {
      window.scroll(0, this.scrollMarker.current!.getBoundingClientRect().top);
    }
  };
}
